import { useProfileAndTabParams } from "@/hooks";
import { useProfile } from "@/reducer/ProfileState";
import { usePresentTab } from "@/reducer/TabsState";
import { buildTabSnapshot } from "@/utils/tabSnapshot";
import type { TabSnapshot } from "@mm/metx-workbench.meteomatics.com";
import { useMemo } from "react";

export type WorkspaceContextSnapshot = {
  message: string;
  metadata: Record<string, unknown>;
  tabSnapshot: TabSnapshot | null;
};

export const useWorkspaceAiContext = (): WorkspaceContextSnapshot | null => {
  const { profileId, tabId } = useProfileAndTabParams();
  const profile = useProfile(profileId);
  const presentTab = usePresentTab();

  return useMemo(() => {
    if (!Number.isFinite(profileId) || !Number.isFinite(tabId)) {
      return null;
    }
    if (!presentTab || !presentTab.tabMeta?.data) {
      return null;
    }

    const tabSnapshot = buildTabSnapshot(presentTab);
    const workspaceLink = `/workspace/${profileId}/${tabId}`;
    const tabTitle =
      presentTab.tabMeta.data?.title || profile?.tabs?.find((tab) => tab.id === tabId)?.title || "Untitled tab";
    const profileTitle = profile?.title || "Untitled dashboard";

    const lines: string[] = [
      "[CurrentWorkspaceContext]",
      `profile_id: ${profileId} (${profileTitle})`,
      `tab_id: ${tabId} (${tabTitle})`,
      `workspace_link: ${workspaceLink}`,
    ];

    return {
      message: lines.join("\n"),
      metadata: {
        activeProfileId: profileId,
        activeTabId: tabId,
        workspaceLink,
        tabTitle,
        profileTitle,
      },
      tabSnapshot,
    };
  }, [profileId, tabId, profile, presentTab]);
};
